import { useEffect, useState } from 'react';

interface TrustBadge {
  icon: string;
  label: string;
}

interface ServiceHeroProps {
  bgImage: string;
  label: string;
  headline: string;
  headlineAccent: string;
  subtext: string;
  trustBadges?: TrustBadge[];
}

const defaultBadges: TrustBadge[] = [
  { icon: 'ri-shield-star-fill', label: 'Licensed & Insured' },
  { icon: 'ri-code-s-slash-fill', label: 'Code-Compliant Work' },
  { icon: 'ri-award-fill', label: '20+ Years Experience' },
  { icon: 'ri-chat-check-fill', label: 'Clear Communication' },
];

const ServiceHero = ({
  bgImage,
  label,
  headline,
  headlineAccent,
  subtext,
  trustBadges = defaultBadges,
}: ServiceHeroProps) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full min-h-[88vh] flex items-center overflow-hidden">
      {/* BG image */}
      <div className="absolute inset-0">
        <img src={bgImage} alt={headline} className="w-full h-full object-cover object-center" />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#060E1C]/96 via-[#0A1628]/85 to-[#0A1628]/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#060E1C]/70 via-transparent to-[#060E1C]/30" />

      {/* Circuit overlay */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="svc-circuit" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M0 50 h30 M70 50 h30 M50 0 v30 M50 70 v30" stroke="#00D9FF" strokeWidth="0.6" fill="none"/>
              <circle cx="50" cy="50" r="4" stroke="#00D9FF" strokeWidth="0.6" fill="none"/>
              <circle cx="30" cy="50" r="2" fill="#00D9FF" opacity="0.6"/>
              <circle cx="70" cy="50" r="2" fill="#00D9FF" opacity="0.6"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#svc-circuit)"/>
        </svg>
      </div>

      {/* Bottom glow */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00D9FF]/30 to-transparent" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 lg:pb-28">
        <div className="max-w-2xl lg:max-w-3xl">
          {/* Label */}
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="inline-flex items-center gap-2 text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-5">
              <span className="w-5 h-[1px] bg-[#00D9FF]" />
              {label}
              <span className="w-5 h-[1px] bg-[#00D9FF]" />
            </span>
          </div>

          {/* Headline */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h1 className="text-4xl lg:text-6xl font-black leading-[1.08] mb-5 text-white">
              {headline}{' '}
              <span className="relative inline-block">
                <span className="text-[#00D9FF] [text-shadow:0_0_36px_rgba(0,217,255,0.45)]">{headlineAccent}</span>
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-[#00D9FF] to-transparent rounded-full" />
              </span>
            </h1>
          </div>

          {/* Subtext */}
          <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p className="text-[#8BA8C8] text-lg leading-relaxed max-w-xl mb-10">{subtext}</p>
          </div>

          {/* CTAs */}
          <div className={`transition-all duration-700 delay-400 flex flex-col sm:flex-row gap-4 mb-12 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <a
              href="#estimate"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#00D9FF] hover:bg-[#00c5e8] text-[#0A1628] font-bold text-[14px] uppercase tracking-wider rounded-lg transition-all duration-300 hover:shadow-[0_0_36px_rgba(0,217,255,0.5)] hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
            >
              Request a Free Estimate
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </a>
            <a
              href="tel:+12693700765"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-white/25 hover:border-white/50 text-white font-semibold text-[14px] rounded-lg transition-all duration-300 hover:bg-white/5 cursor-pointer whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-[#00D9FF]" />
              </span>
              Call (269) 370-0765
            </a>
          </div>

          {/* Trust badges */}
          <div className={`transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex flex-wrap gap-2.5">
              {trustBadges.map((badge) => (
                <div
                  key={badge.label}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:border-[#00D9FF]/40 transition-all duration-300"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-[#00D9FF] text-sm`} />
                  </span>
                  <span className="text-white text-[12px] font-semibold tracking-wide whitespace-nowrap">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceHero;
